import React, { useEffect, useState } from 'react';

// Ops Knowledge Base — deployment/operations lessons learned.
// Visible ONLY to platform super admins (menu gated in Sidebar,
// API gated by requireSuperAdminOnly on the backend).

type KbArticle = {
  id: number;
  title: string;
  category: string;
  severity: string;
  symptom: string;
  root_cause: string;
  resolution: string;
  prevention: string;
  tags: string;
  created_by?: string;
  created_at?: string;
};

const SEV_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high:     '#ea580c',
  medium:   '#ca8a04',
  low:      '#16a34a',
};

const EMPTY_DRAFT = {
  title: '', category: 'Telephony', severity: 'medium',
  symptom: '', root_cause: '', resolution: '', prevention: '', tags: ''
};

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export default function OpsKnowledgeBase() {
  const [articles, setArticles] = useState<KbArticle[]>([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<any>({ ...EMPTY_DRAFT });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [status, setStatus] = useState('');

  const load = async () => {
    try {
      const r = await fetch('/api/admin/kb', { headers: authHeaders() });
      if (r.ok) setArticles(await r.json());
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const set = (k: string, v: string) => setDraft((d: any) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft.title.trim()) { alert('Title required'); return; }
    const url = editingId ? `/api/admin/kb/${editingId}` : '/api/admin/kb';
    const method = editingId ? 'PUT' : 'POST';
    const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(draft) });
    if (r.ok) {
      setStatus(editingId ? 'Article updated' : 'Article added');
      setDraft({ ...EMPTY_DRAFT }); setEditingId(null); setShowForm(false);
      await load();
    } else {
      alert('Save failed');
    }
  };

  const startEdit = (a: KbArticle) => {
    setDraft({ title: a.title, category: a.category, severity: a.severity,
      symptom: a.symptom || '', root_cause: a.root_cause || '',
      resolution: a.resolution || '', prevention: a.prevention || '', tags: a.tags || '' });
    setEditingId(a.id); setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (a: KbArticle) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await fetch(`/api/admin/kb/${a.id}`, { method: 'DELETE', headers: authHeaders() });
    await load();
  };

  const q = search.toLowerCase();
  const filtered = articles.filter((a) =>
    !q ||
    a.title.toLowerCase().includes(q) ||
    (a.tags || '').toLowerCase().includes(q) ||
    (a.symptom || '').toLowerCase().includes(q) ||
    (a.category || '').toLowerCase().includes(q)
  );

  const categories = Array.from(new Set(filtered.map((a) => a.category || 'General')));

  const Section = ({ label, text }: { label: string; text: string }) =>
    text ? (
      <div style={{ marginBottom: '10px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '13px', color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{text}</div>
      </div>
    ) : null;

  return (
    <>
      <div className="admin-page-title">Ops Knowledge Base</div>
      <div className="admin-page-subtitle">
        Deployment &amp; operations lessons learned — issues faced on this platform and how they were resolved. Super admin only.
      </div>

      {status && <div className="admin-alert">{status}</div>}

      <div className="content-card">
        <div className="admin-card-header">
          <h3>Articles <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 400 }}>({filtered.length})</span></h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input className="admin-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, symptom, tags…" />
            <button className="primary-btn" onClick={() => { setShowForm(!showForm); setEditingId(null); setDraft({ ...EMPTY_DRAFT }); }}>
              {showForm ? 'Close' : '+ Add Article'}
            </button>
          </div>
        </div>

        {showForm && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', marginBottom: '16px', background: '#f9fafb' }}>
            <div className="admin-form-row">
              <input className="admin-input" style={{ flex: 2 }} value={draft.title} onChange={(e) => set('title', e.target.value)} placeholder="Title *" />
              <input className="admin-input" value={draft.category} onChange={(e) => set('category', e.target.value)} placeholder="Category" />
              <select className="admin-select" value={draft.severity} onChange={(e) => set('severity', e.target.value)}>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <textarea className="admin-input" style={{ width: '100%', minHeight: '54px', marginBottom: '8px' }} value={draft.symptom} onChange={(e) => set('symptom', e.target.value)} placeholder="Symptom — what was observed" />
            <textarea className="admin-input" style={{ width: '100%', minHeight: '54px', marginBottom: '8px' }} value={draft.root_cause} onChange={(e) => set('root_cause', e.target.value)} placeholder="Root cause" />
            <textarea className="admin-input" style={{ width: '100%', minHeight: '54px', marginBottom: '8px' }} value={draft.resolution} onChange={(e) => set('resolution', e.target.value)} placeholder="Resolution — what fixed it" />
            <textarea className="admin-input" style={{ width: '100%', minHeight: '54px', marginBottom: '8px' }} value={draft.prevention} onChange={(e) => set('prevention', e.target.value)} placeholder="Prevention — how to avoid it in future" />
            <div className="admin-form-row">
              <input className="admin-input" style={{ flex: 1 }} value={draft.tags} onChange={(e) => set('tags', e.target.value)} placeholder="Tags (comma separated)" />
              <button className="primary-btn" onClick={save}>{editingId ? 'Update Article' : 'Save Article'}</button>
            </div>
          </div>
        )}

        {categories.map((cat) => (
          <div key={cat} style={{ marginBottom: '18px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151', margin: '10px 0 8px' }}>{cat}</div>
            {filtered.filter((a) => (a.category || 'General') === cat).map((a) => (
              <div key={a.id} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', marginBottom: '8px', overflow: 'hidden' }}>
                <div
                  onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer', background: expanded === a.id ? '#f3f4f6' : '#fff' }}
                >
                  <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: SEV_COLORS[a.severity] || '#6b7280', flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, fontSize: '14px', flex: 1 }}>{a.title}</span>
                  <span style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase' }}>{a.severity}</span>
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>{expanded === a.id ? '▲' : '▼'}</span>
                </div>
                {expanded === a.id && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
                    <Section label="Symptom" text={a.symptom} />
                    <Section label="Root Cause" text={a.root_cause} />
                    <Section label="Resolution" text={a.resolution} />
                    <Section label="Prevention" text={a.prevention} />
                    {a.tags && (
                      <div style={{ marginTop: '6px' }}>
                        {a.tags.split(',').map((t) => (
                          <span key={t} style={{ display: 'inline-block', fontSize: '11px', background: '#eef2ff', color: '#4f46e5', borderRadius: '20px', padding: '2px 10px', marginRight: '6px' }}>{t.trim()}</span>
                        ))}
                      </div>
                    )}
                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                      <button className="secondary-btn" onClick={() => startEdit(a)}>Edit</button>
                      <button className="secondary-btn" style={{ color: '#dc2626' }} onClick={() => remove(a)}>Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '30px' }}>No articles found.</div>
        )}
      </div>
    </>
  );
}
